import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash-es/cloneDeep';

import { WhatsappTemplate } from '@/shared/types/broadcast';
import { AmdOnMachine, Campaign, VoiceCallSettings } from '@/shared/types/campaigns';
import { Option } from '@/shared/types/rules';

import { ColumnMapping } from '../types/column-mapping';

/**
 * Subscriber data structure for campaign setup
 */
export interface DraftCampaignSubscriber {
  email?: string;
  phone?: string;
  error?: string; // Validation error message if subscriber has errors
  errorField?: string; // Raw error field code from verification API
  [key: string]: string | undefined;
}

/**
 * Draft campaign data structure for setup process
 */
export interface DraftCampaign {
  name: string;
  type: string;
  subscribers_context: Record<string, unknown>;
  status: string;
  schedule: { start_at: string };
  brain_parent_id: string | null;
  brain_version: number;
  brain_name?: string;
  created: string;
  subscribers: DraftCampaignSubscriber[];
  fileName?: string;
  fileSize?: number;
  tags?: { label: string; value: string }[];
  buildData?: Record<string, unknown>;
  compliance_profile_id?: string;
  autonomous_actions?: boolean;
}

/**
 * Audience stats computed from subscribers (stored in campaign metadata)
 * Note: total_subscribers is NOT stored here - use campaign.subscribers_count as source of truth
 */
export interface AudienceStats {
  email_subscribers: number;
  phone_subscribers: number;
  total_amount_due: number;
  variables_count: number;
}

/** Voice opener settings tagged with the campaign they were authored on */
export interface DraftVoiceSettings {
  campaignId: string;
  settings: VoiceCallSettings;
}

/**
 * Draft campaign sender data structure
 */
export interface DraftCampaignSender {
  senderId?: string; // The sender's unique ID (for matching transitions)
  integrationId: string;
  integrationType: string;
  content: string;
  subject?: string;
  template?: WhatsappTemplate;
  enabled?: boolean;
  waitTimeMinutes?: number;
  order?: number;
  transitions?: Array<{ condition: string; after_minutes: number; sender_id?: string }>;
  headerParameters?: Array<{ name: string }>;
  bodyParameters?: Array<{ name: string }>;
  footerParameters?: Array<{ name: string }>;
  headerMediaParam?: { type: 'id' | 'link'; value: string | Option };
  amdOnMachine?: AmdOnMachine;
}

export interface CampaignsState {
  campaigns: Campaign[];
  selectedCampaign: Campaign | null;
  draftCampaign: Partial<DraftCampaign> | null;

  // Separate subscribers state
  draftSubscribers: DraftCampaignSubscriber[];
  subscribersFileName?: string;
  subscribersFileSize?: number;

  // Payment recovery campaign draft state (shared between Build and Message pages)
  // Note: Payment recovery only has a single sender (no primary/fallback like outreach campaigns)
  draftPaymentRecoverySender: DraftCampaignSender | null;
  // Sequential senders for payment recovery
  // Index 0: primary sender, Index 1: WA follow-up template, Index 2: Email follow-up subject
  draftPaymentRecoverySenders: DraftCampaignSender[] | null;
  draftPaymentRecoveryGoal: string | null;
  draftPaymentRecoveryGuidelines: string | null;
  draftOutreachGoal: string | null;

  // Campaign-level voice opener settings. The NBA message page and the page
  // that saves the senders run separate forms, so they meet here. Scoped to a
  // campaign because the draft outlives a campaign switch, and these values
  // end up in sender metadata: unscoped, campaign A's delay would follow the
  // user into campaign B.
  draftVoiceSettings: DraftVoiceSettings | null;

  // Selected channels for NBA messaging (persists across navigation)
  draftSelectedChannels: string[] | null;

  // Custom tags created during campaign setup
  customTags: { label: string; value: string }[];

  // Audience stats computed from uploaded subscribers
  audienceStats: AudienceStats | null;

  // CSV mapping data for inline re-transformation on Audience page
  csvHeaders?: string[];
  csvMappings?: Record<string, ColumnMapping>;
  csvRows?: string[][]; // Limit to 10k rows for performance
  /**
   * Subscriber-context emit paths the user marked for mirroring under
   * `context.live_instructions.*` (MOV-15472). Persists alongside the CSV
   * mapping so re-uploads / re-mappings preserve the selection.
   */
  csvLiveInstructionTargets?: string[];

  isLoading: boolean;
  error: string | null;
}

const emptyCampaign = { aiAgent: '', tags: [] as { label: string; value: string }[] };

export const defaultState: CampaignsState = {
  campaigns: [],
  selectedCampaign: null,
  draftCampaign: emptyCampaign,
  draftSubscribers: [],
  subscribersFileName: undefined,
  subscribersFileSize: undefined,
  draftPaymentRecoverySender: null,
  draftPaymentRecoverySenders: null,
  draftPaymentRecoveryGoal: null,
  draftPaymentRecoveryGuidelines: null,
  draftOutreachGoal: null,
  draftVoiceSettings: null,
  draftSelectedChannels: null,
  customTags: [],
  audienceStats: null,
  csvHeaders: undefined,
  csvMappings: undefined,
  csvRows: undefined,
  csvLiveInstructionTargets: undefined,
  isLoading: false,
  error: null,
};

// Clone so Immer freezes the copy, not react-hook-form's live arrays (which it would
// then fail to write to in place). cloneDeep(null) is null.
const cloneSenderPayload = <T>(payload: T): T => cloneDeep(payload);

const slice = createSlice({
  name: 'campaigns',
  initialState: defaultState,
  reducers: {
    /**
     * Set subscribers for the draft campaign
     */
    setDraftSubscribers: (
      state,
      action: PayloadAction<{
        subscribers: DraftCampaignSubscriber[];
        fileName?: string;
        fileSize?: number;
      }>
    ) => {
      state.draftSubscribers = action.payload.subscribers;
      if (action.payload?.fileName) {
        state.subscribersFileName = action.payload.fileName;
      }
      if (action.payload?.fileSize) {
        state.subscribersFileSize = action.payload.fileSize;
      }
    },

    /**
     * Update draft campaign data
     */
    updateDraftCampaign: (state, action: PayloadAction<Partial<DraftCampaign>>) => {
      state.draftCampaign = { ...state.draftCampaign, ...action.payload };
    },

    /**
     * Clear draft campaign data
     */
    clearDraftCampaign: (state) => {
      state.draftCampaign = emptyCampaign;
      state.selectedCampaign = null;
      state.draftSubscribers = [];
      state.subscribersFileName = undefined;
      state.subscribersFileSize = undefined;
      state.draftPaymentRecoverySender = null;
      state.draftPaymentRecoverySenders = null;
      state.draftPaymentRecoveryGoal = null;
      state.draftPaymentRecoveryGuidelines = null;
      state.draftOutreachGoal = null;
      state.draftVoiceSettings = null;
      state.draftSelectedChannels = null;
      state.customTags = [];
      state.csvHeaders = undefined;
      state.csvMappings = undefined;
      state.csvRows = undefined;
      state.csvLiveInstructionTargets = undefined;
    },

    /**
     * Set payment recovery draft sender (single sender, no primary/fallback)
     */
    setDraftPaymentRecoverySender: (state, action: PayloadAction<DraftCampaignSender | null>) => {
      state.draftPaymentRecoverySender = cloneSenderPayload(action.payload);
    },

    /**
     * Update payment recovery draft sender (merges with existing)
     */
    updateDraftPaymentRecoverySender: (
      state,
      action: PayloadAction<Partial<DraftCampaignSender>>
    ) => {
      const data = cloneSenderPayload(action.payload);
      if (state.draftPaymentRecoverySender === null) {
        state.draftPaymentRecoverySender = data as DraftCampaignSender;
      } else {
        state.draftPaymentRecoverySender = { ...state.draftPaymentRecoverySender, ...data };
      }
    },

    /**
     * Clear payment recovery draft sender
     */
    clearDraftPaymentRecoverySender: (state) => {
      state.draftPaymentRecoverySender = null;
    },

    /**
     * Set multi-channel payment recovery draft senders (replace entire array)
     */
    setDraftPaymentRecoverySenders: (
      state,
      action: PayloadAction<DraftCampaignSender[] | null>
    ) => {
      state.draftPaymentRecoverySenders = cloneSenderPayload(action.payload);
    },

    /**
     * Update a specific sender in the multi-channel senders array by index
     */
    updateDraftPaymentRecoverySenderAt: (
      state,
      action: PayloadAction<{ index: number; data: Partial<DraftCampaignSender> }>
    ) => {
      const { index, data } = action.payload;
      if (state.draftPaymentRecoverySenders && state.draftPaymentRecoverySenders[index]) {
        state.draftPaymentRecoverySenders[index] = {
          ...state.draftPaymentRecoverySenders[index],
          ...cloneSenderPayload(data),
        };
      }
    },

    /**
     * Set autonomous actions flag for unified campaign setup
     */
    setDraftAutonomousActions: (state, action: PayloadAction<boolean>) => {
      if (state.draftCampaign) {
        state.draftCampaign.autonomous_actions = action.payload;
      }
    },

    /**
     * Set selected channels for NBA messaging (persists across navigation)
     */
    setDraftSelectedChannels: (state, action: PayloadAction<string[] | null>) => {
      state.draftSelectedChannels = action.payload;
    },

    /** Set payment recovery draft goal */
    setDraftPaymentRecoveryGoal: (state, action: PayloadAction<string | null>) => {
      state.draftPaymentRecoveryGoal = action.payload;
    },

    /**
     * Set payment recovery draft guidelines (markdown string)
     */
    setDraftPaymentRecoveryGuidelines: (state, action: PayloadAction<string | null>) => {
      state.draftPaymentRecoveryGuidelines = action.payload;
    },
    /**
     * Set the campaign-level voice opener settings for one campaign
     */
    setDraftVoiceSettings: (state, action: PayloadAction<DraftVoiceSettings | null>) => {
      state.draftVoiceSettings = action.payload;
    },
    setDraftOutreachGoal: (state, action: PayloadAction<string | null>) => {
      state.draftOutreachGoal = action.payload;
    },
    setDraftCampaign: (state, action: PayloadAction<Partial<DraftCampaign>>) => {
      state.draftCampaign = { ...state.draftCampaign, ...action.payload };
    },
    setSelectedCampaign: (state, action: PayloadAction<Campaign>) => {
      state.selectedCampaign = action.payload;
    },
    /**
     * Merge an updated campaign into selectedCampaign (only when ids match),
     * keeping Redux in sync after update mutations.
     */
    mergeSelectedCampaign: (state, action: PayloadAction<Partial<Campaign>>) => {
      if (
        action.payload.campaign_id != null &&
        state.selectedCampaign?.campaign_id === action.payload.campaign_id
      ) {
        state.selectedCampaign = { ...state.selectedCampaign, ...action.payload };
      }
    },
    /**
     * Clear subscribers from draft campaign
     */
    clearDraftSubscribers: (state) => {
      state.draftSubscribers = [];
      state.subscribersFileName = undefined;
      state.subscribersFileSize = undefined;
      state.audienceStats = null;
      state.csvHeaders = undefined;
      state.csvMappings = undefined;
      state.csvRows = undefined;
      state.csvLiveInstructionTargets = undefined;
    },

    /**
     * Set audience stats for the draft campaign
     */
    setDraftAudienceStats: (state, action: PayloadAction<AudienceStats>) => {
      state.audienceStats = action.payload;
    },

    /**
     * Set CSV mapping data for inline re-transformation
     */
    setCsvMappingData: (
      state,
      action: PayloadAction<{
        headers: string[];
        mappings: Record<string, ColumnMapping>;
        rows: string[][];
      }>
    ) => {
      state.csvHeaders = action.payload.headers;
      state.csvMappings = action.payload.mappings;
      state.csvRows = action.payload.rows;
    },

    /**
     * Replace the set of subscriber-context emit paths the user wants
     * mirrored under `context.live_instructions.*`. Set from the Match
     * Fields modal whenever mappings are confirmed.
     */
    setCsvLiveInstructionTargets: (state, action: PayloadAction<string[]>) => {
      state.csvLiveInstructionTargets = action.payload;
    },

    /**
     * Clear CSV mapping data
     */
    clearCsvMappingData: (state) => {
      state.csvHeaders = undefined;
      state.csvMappings = undefined;
      state.csvRows = undefined;
      state.csvLiveInstructionTargets = undefined;
    },

    /**
     * Add a custom tag that persists across navigation
     */
    addCustomTag: (state, action: PayloadAction<{ label: string; value: string }>) => {
      const exists = state.customTags.some((tag) => tag.value === action.payload.value);
      if (!exists) {
        state.customTags.push(action.payload);
      }
    },

    /**
     * Clear custom tags
     */
    clearCustomTags: (state) => {
      state.customTags = [];
    },
  },
});

// Export actions
export const {
  setDraftSubscribers,
  updateDraftCampaign,
  clearDraftCampaign,
  clearDraftSubscribers,
  setDraftCampaign,
  setSelectedCampaign,
  mergeSelectedCampaign,
  addCustomTag,
  clearCustomTags,
  setDraftPaymentRecoverySender,
  updateDraftPaymentRecoverySender,
  clearDraftPaymentRecoverySender,
  setDraftPaymentRecoverySenders,
  updateDraftPaymentRecoverySenderAt,
  setDraftAutonomousActions,
  setDraftSelectedChannels,
  setDraftPaymentRecoveryGoal,
  setDraftPaymentRecoveryGuidelines,
  setDraftVoiceSettings,
  setDraftOutreachGoal,
  setDraftAudienceStats,
  setCsvMappingData,
  setCsvLiveInstructionTargets,
  clearCsvMappingData,
} = slice.actions;

// Export the reducer
export default slice.reducer;
